import dayjs from 'dayjs'
import { getPublicDataWithoutParams } from 'src/consumer/services/featureServices'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'

const CACHE = new Map()
const MAX_AGE_MS = 5 * 60 * 1000 // 5 minutes

function cacheKey(mentorSlug, eventId, from, to, timeZone) {
  return `${mentorSlug}|${eventId}|${from}|${to}|${timeZone}`
}

/**
 * Returns cached booking page data if present and not stale.
 * @returns {{ bookingPageData, profile, normalizedSlots } | null}
 */
export function getCachedBookingPage(mentorSlug, eventId) {
  const from = dayjs().format('YYYY-MM-DD')
  const to = dayjs(from).add(42, 'day').format('YYYY-MM-DD')
  const timeZone = getTimezoneOffset()
  const key = cacheKey(mentorSlug, eventId, from, to, timeZone)
  const entry = CACHE.get(key)
  if (!entry || Date.now() - entry.timestamp > MAX_AGE_MS) {
    if (entry) CACHE.delete(key)
    return null
  }
  return entry.data
}

/**
 * Store booking page data for the initial date range (today + 42 days).
 * Used by the booking page when it has data so a later remount could reuse (optional).
 */
export function setCachedBookingPage(mentorSlug, eventId, data) {
  const from = dayjs().format('YYYY-MM-DD')
  const to = dayjs(from).add(42, 'day').format('YYYY-MM-DD')
  const timeZone = getTimezoneOffset()
  const key = cacheKey(mentorSlug, eventId, from, to, timeZone)
  CACHE.set(key, { data, timestamp: Date.now() })
}

/**
 * Prefetch mentor-booking-page and profile for the given mentor/event.
 * Call from the events listing when user expands an event or when listing loads.
 * Stored data can be consumed by PublicComponentEventBooking via getCachedBookingPage().
 */
export async function prefetchBookingPage(mentorSlug, eventId) {
  if (!mentorSlug || !eventId) return
  const from = dayjs().format('YYYY-MM-DD')
  const to = dayjs(from).add(42, 'day').format('YYYY-MM-DD')
  const timeZone = getTimezoneOffset()
  const key = cacheKey(mentorSlug, eventId, from, to, timeZone)
  if (CACHE.has(key)) return

  try {
    const params = `mentorSlug=${encodeURIComponent(mentorSlug)}&eventId=${encodeURIComponent(eventId)}&from=${from}&to=${to}&timeZone=${encodeURIComponent(timeZone)}`
    const res = await getPublicDataWithoutParams(
      `/api/public/mentor-booking-page?${params}`,
    )
    const status = res?.response?.status ?? res?.status
    const body = res?.response?.data ?? res?.data
    const code = body?.code
    if (status !== 200 || !isCommonResponseSuccessful(code)) return

    const data = body?.data
    const bookingPage = data?.bookingPage
    if (!bookingPage?.mentor || !bookingPage?.event) return

    const normalizedSlots = (bookingPage.availableSlots || []).map(({ date, slots }) => ({
      date,
      availableTimeSlots: Array.isArray(slots) ? slots : [],
    }))

    const mentorRef = bookingPage.mentor
    const profileRes = await getPublicDataWithoutParams(
      `/api/service-provider?id=${encodeURIComponent(mentorRef.id)}&slug=${encodeURIComponent(mentorRef.slug || '')}&include=heroCardImage`,
    )
    const profileStatus = profileRes?.response?.status ?? profileRes?.status
    const profile = profileStatus === 200 ? profileRes?.data : null
    const professionalData =
      profile && typeof profile === 'object'
        ? { ...profile, eventTypes: [bookingPage.event] }
        : { ...mentorRef, eventTypes: [bookingPage.event] }

    CACHE.set(key, {
      data: {
        bookingPageData: data,
        professionalData,
        normalizedSlots,
      },
      timestamp: Date.now(),
    })
  } catch (_) {
    // Prefetch is best-effort; ignore errors
  }
}
